import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import {
  DeployEnvironment,
  DEPLOY_ENVIRONMENT_LABELS,
  deployQuest as deployQuestAsync,
  deployQuestToEnvironments,
  PRODUCTION_ENVIRONMENTS,
} from "../../api/quests/quests";
import { useNodeTypesProvider } from "../../context/reactflow/NodeTypesProvider";
import useExportRuntimeData from "../useExportRuntimeData";

interface DeployQuest {
  questId: string;
  environment: DeployEnvironment;
}

interface DeployQuestToProduction {
  questId: string;
}

export default function useDeployQuest() {
  const { exportQuestNodes } = useExportRuntimeData();

  const toast = useToast();

  const { nodeTypes } = useNodeTypesProvider();

  const { isPending: isDeploying, mutate: deployQuest } = useMutation({
    mutationFn: ({ questId, environment }: DeployQuest) =>
      deployQuestAsync(questId, environment, exportQuestNodes, nodeTypes),
    onSuccess: () => {
      toast({ title: "Quest deploy successful", status: "success" });
    },
    onError: (error) => {
      toast({ title: "Quest deploy unsuccessful", status: "error", description: error.message });
    },
  });

  const { isPending: isDeployingToProduction, mutateAsync: deployToProduction } = useMutation({
    mutationFn: ({ questId }: DeployQuestToProduction) =>
      deployQuestToEnvironments(questId, PRODUCTION_ENVIRONMENTS, exportQuestNodes, nodeTypes),
    onSuccess: (results) => {
      const failed = results.filter(({ ok }) => !ok);

      if (failed.length === 0) {
        toast({ title: "Deployed to all production regions", status: "success" });
      } else {
        toast({
          title: `Deployed to ${results.length - failed.length}/${results.length} production regions`,
          status: "warning",
          description: failed.map(({ environment, error }) => `${DEPLOY_ENVIRONMENT_LABELS[environment]}: ${error}`).join("; "),
        });
      }
    },
    onError: (error) => {
      toast({ title: "Production deploy failed", status: "error", description: error.message });
    },
  });

  return {
    isDeploying,
    deployQuest,
    isDeployingToProduction,
    deployToProduction,
  };
}
